import React, { useState, useEffect } from 'react'
import { TimeEntry, Project } from '../../types'
import { X, Clock, Calendar, AlertCircle, Edit3, PlusCircle } from 'lucide-react'
import toast from 'react-hot-toast'

interface EditTimeEntryModalProps {
  isOpen: boolean
  entry: TimeEntry | null
  onClose: () => void
  onSubmit: (data: EditTimeEntryData) => Promise<void>
  projects: Project[]
  isLoading?: boolean
}

export interface EditTimeEntryData {
  id: string
  projectId: string
  description: string
  startTime: string
  endTime: string
  originalDate?: string
}

const EditTimeEntryModal: React.FC<EditTimeEntryModalProps> = ({
  isOpen,
  entry,
  onClose,
  onSubmit,
  projects,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<EditTimeEntryData>({
    id: '',
    projectId: '',
    description: '',
    startTime: '09:00',
    endTime: '17:00'
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [calculatedDuration, setCalculatedDuration] = useState<string>('')

  // 當 entry 變更時初始化表單資料
  useEffect(() => {
    if (entry && isOpen) {
      const formatTime = (dateTime: string) => {
        const date = new Date(dateTime)
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      setFormData({
        id: entry.id,
        projectId: entry.projectId,
        description: entry.description || '',
        startTime: formatTime(entry.startTime),
        endTime: entry.endTime ? formatTime(entry.endTime) : '17:00'
      })
    }
  }, [entry, isOpen])

  // 計算工時
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end > start) {
        const diffMs = end.getTime() - start.getTime()
        const hours = Math.floor(diffMs / (1000 * 60 * 60))
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
        setCalculatedDuration(`${hours}h ${minutes}m`)
      } else {
        setCalculatedDuration('無效時間')
      }
    }
  }, [formData.startTime, formData.endTime])

  // 表單驗證
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.projectId) {
      newErrors.projectId = '請選擇專案'
    }

    if (!formData.startTime) {
      newErrors.startTime = '請選擇開始時間'
    }

    if (!formData.endTime) {
      newErrors.endTime = '請選擇結束時間'
    }

    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end <= start) {
        newErrors.endTime = '結束時間必須晚於開始時間'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
      handleClose()
      toast.success('時間記錄已更新')
    } catch (error) {
      toast.error('更新時間記錄失敗')
    }
  }

  // 處理關閉
  const handleClose = () => {
    setFormData({
      id: '',
      projectId: '',
      description: '',
      startTime: '09:00',
      endTime: '17:00'
    })
    setErrors({})
    setCalculatedDuration('')
    onClose()
  }

  // 處理輸入變更
  const handleInputChange = (field: keyof EditTimeEntryData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  if (!isOpen || !entry) return null

  // 格式化日期顯示
  const formatDate = (dateTime: string) => {
    const date = new Date(dateTime)
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="glass-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200/50
          bg-gradient-to-r from-blue-50 to-indigo-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Edit3 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-secondary-800">編輯記錄</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-secondary-400 hover:text-secondary-600 
              hover:bg-secondary-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* 日期顯示 */}
          <div className="flex items-center gap-3 p-4 rounded-xl
            bg-gradient-to-r from-blue-50 to-indigo-50/50 border border-blue-200/50">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-blue-600 font-medium">編輯日期</div>
              <div className="text-sm font-semibold text-blue-700">{formatDate(entry.startTime)}</div>
            </div>
          </div>

          {/* 專案選擇 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              專案選擇 <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => handleInputChange('projectId', e.target.value)}
              className={`input-modern ${errors.projectId ? 'border-red-400 focus:border-red-500' : ''}`}
            >
              <option value="">選擇專案...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.projectId && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {errors.projectId}
              </p>
            )}
          </div>

          {/* 工作描述 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              工作描述
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="輸入工作內容..."
              className="input-modern resize-none"
              rows={2}
            />
          </div>

          {/* 時間設定 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                開始時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={`input-modern ${errors.startTime ? 'border-red-400 focus:border-red-500' : ''}`}
              />
              {errors.startTime && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.startTime}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                結束時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={`input-modern ${errors.endTime ? 'border-red-400 focus:border-red-500' : ''}`}
              />
              {errors.endTime && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.endTime}
                </p>
              )}
            </div>
          </div>

          {/* 計算工時顯示 */}
          {calculatedDuration && (
            <div className="flex items-center gap-3 p-4 rounded-xl
              bg-gradient-to-r from-green-50 to-emerald-50/50 border border-green-200/50">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-xs text-green-600 font-medium">計算工時</div>
                <div className="text-lg font-bold text-green-700 font-mono">{calculatedDuration}</div>
              </div>
            </div>
          )}

          {/* 標籤顯示 */}
          {(entry.isManual || entry.isEdited) && (
            <div className="flex flex-wrap gap-2">
              {entry.isManual && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg 
                  text-xs font-medium bg-orange-100 text-orange-700">
                  <PlusCircle className="w-3.5 h-3.5" />
                  手動新增
                </span>
              )}
              {entry.isEdited && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg 
                  text-xs font-medium bg-blue-100 text-blue-700">
                  <Edit3 className="w-3.5 h-3.5" />
                  已編輯
                </span>
              )}
            </div>
          )}

          {/* 提示訊息 */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50/50 
            border border-amber-200/50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 
                flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">注意事項</p>
                <ul className="space-y-0.5 text-amber-700">
                  <li>• 編輯後此記錄將標記為「已編輯」</li>
                  <li>• 結束時間必須晚於開始時間</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-secondary-200/50 bg-secondary-50/30">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-5 py-3 rounded-xl font-medium
              text-secondary-700 bg-white border border-secondary-200
              hover:bg-secondary-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 px-5 py-3 rounded-xl font-medium text-white
              bg-gradient-to-r from-blue-500 to-blue-600 
              hover:from-blue-600 hover:to-blue-700
              shadow-lg shadow-blue-200/50 hover:shadow-xl
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                更新中...
              </span>
            ) : (
              '確認更新'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditTimeEntryModal
